/**
 * Readiness Alert Settings Component
 *
 * Per-team controls for coach readiness threshold alerts.
 * Rendered inside SettingsPage alongside SessionLoggingSettings.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';
import { authenticatedFetch } from '@/utils/apiUtils';

interface ReadinessAlertSettingsProps {
  teamId: number;
  teamName: string;
}

interface Athlete {
  id: number;
  full_name: string;
  email: string;
}

interface AlertSettings {
  threshold: number;
  enabled: boolean;
  cooldown_hours: number;
  selected_athlete_ids: number[];
  target_athlete_count: number;
  updated_at?: string;
}

const DEFAULTS: AlertSettings = {
  threshold: 60,
  enabled: false,
  cooldown_hours: 24,
  selected_athlete_ids: [],
  target_athlete_count: 0,
};

const ReadinessAlertSettings: React.FC<ReadinessAlertSettingsProps> = ({ teamId, teamName }) => {
  const { isDark } = useTheme();
  const [settings, setSettings] = useState<AlertSettings>(DEFAULTS);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const saveTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      const result = await authenticatedFetch(
        getApiUrl(`/api/teams/teams/${teamId}/readiness-alert-settings/`),
        { method: 'GET' },
        'fetching readiness alert settings',
      );
      if (result.success && result.data) {
        setSettings(result.data);
      }
    } catch {
      // Use defaults on error
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  const fetchAthletes = useCallback(async () => {
    try {
      const result = await authenticatedFetch(
        getApiUrl(`/api/teams/teams/${teamId}/`),
        { method: 'GET' },
        'fetching team athletes',
      );
      if (result.success && result.data) {
        let teamData = result.data;
        if (result.data.data) teamData = result.data.data;

        if (teamData.athletes && Array.isArray(teamData.athletes)) {
          setAthletes(teamData.athletes);
        } else if (teamData.members && Array.isArray(teamData.members)) {
          const athleteMembers = teamData.members.filter((m: any) => m.role === 'athlete');
          setAthletes(athleteMembers.map((m: any) => m.user));
        } else {
          setAthletes([]);
        }
      }
    } catch {
      // Athlete list will be empty
    }
  }, [teamId]);

  useEffect(() => {
    setLoading(true);
    fetchSettings();
    fetchAthletes();
  }, [fetchSettings, fetchAthletes]);

  const patchSettings = useCallback(async (patch: Partial<AlertSettings>) => {
    setSaving(true);
    setError(null);

    try {
      const result = await authenticatedFetch(
        getApiUrl(`/api/teams/teams/${teamId}/readiness-alert-settings/`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        },
        'saving readiness alert settings',
      );

      if (result.success && result.data) {
        setSettings(result.data);
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      } else {
        setError(result.error || 'Failed to save');
      }
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  }, [teamId]);

  const handleToggle = () => {
    const next = !settings.enabled;
    setSettings(prev => ({ ...prev, enabled: next }));
    patchSettings({ enabled: next });
  };

  const handleThresholdChange = (value: number) => {
    setSettings(prev => ({ ...prev, threshold: value }));
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => patchSettings({ threshold: value }), 600);
  };

  const handleCooldownChange = (value: number) => {
    setSettings(prev => ({ ...prev, cooldown_hours: value }));
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => patchSettings({ cooldown_hours: value }), 600);
  };

  const handleAthleteToggle = (athleteId: number) => {
    const next = settings.selected_athlete_ids.includes(athleteId)
      ? settings.selected_athlete_ids.filter(id => id !== athleteId)
      : [...settings.selected_athlete_ids, athleteId];

    setSettings(prev => ({ ...prev, selected_athlete_ids: next }));
    patchSettings({ selected_athlete_ids: next });
  };

  if (loading) {
    return (
      <div className="py-4 cgm-text-secondary">
        Loading settings for {teamName}...
      </div>
    );
  }

  return (
    <div className="mb-6 p-6 cgm-card rounded-lg">
      <h3 className="text-xl font-bold mb-4 cgm-text-primary">{teamName}</h3>

      <div className="space-y-6">
        {/* Enable / disable toggle */}
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium cgm-text-primary">
              Enable Readiness Alerts
            </label>
            <p className="text-sm cgm-text-secondary mt-1">
              {settings.enabled
                ? <>Email me when athletes' readiness drops below <strong>{settings.threshold}</strong></>
                : 'Get emailed when athletes need attention'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
              settings.enabled ? 'bg-green-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
            } ${saving ? 'opacity-50' : ''}`}
            aria-label="Toggle readiness alerts"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Controls visible when enabled */}
        {settings.enabled && (
          <>
            {/* Threshold slider */}
            <div>
              <label className="block text-sm font-medium cgm-text-primary mb-2">
                Readiness Threshold
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={30}
                  max={80}
                  step={5}
                  value={settings.threshold}
                  onChange={e => handleThresholdChange(Number(e.target.value))}
                  className="flex-1 h-2 accent-green-500 cursor-pointer"
                />
                <span className="text-lg font-mono font-semibold w-10 text-center cgm-text-primary">
                  {settings.threshold}
                </span>
              </div>
              <p className="text-sm cgm-text-secondary mt-1">
                Athletes scoring below this value will trigger an alert email
              </p>
            </div>

            {/* Cooldown selector */}
            <div>
              <label className="block text-sm font-medium cgm-text-primary mb-2">
                Cooldown Period
              </label>
              <select
                value={settings.cooldown_hours}
                onChange={e => handleCooldownChange(Number(e.target.value))}
                className="cgm-input mt-1 block w-full px-3 py-2"
              >
                <option value={12}>12 hours</option>
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
                <option value={72}>72 hours (3 days)</option>
                <option value={168}>168 hours (7 days)</option>
              </select>
              <p className="text-sm cgm-text-secondary mt-1">
                Minimum time between repeat alerts for the same athlete
              </p>
            </div>

            {/* Athlete selector */}
            <div>
              <label className="block text-sm font-medium cgm-text-primary mb-2">
                Athletes to Monitor
              </label>
              <div className="cgm-list-item rounded-md p-3 max-h-48 overflow-y-auto">
                {athletes.length === 0 ? (
                  <p className="text-sm cgm-text-secondary">No athletes on this team</p>
                ) : (
                  <div className="space-y-2">
                    {athletes.map((athlete) => (
                      <label key={athlete.id} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.selected_athlete_ids.includes(athlete.id)}
                          onChange={() => handleAthleteToggle(athlete.id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <span className="ml-2 text-sm cgm-text-primary">
                          {athlete.full_name || athlete.email}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-sm cgm-text-secondary mt-1">
                Leave unchecked to monitor all athletes ({athletes.length} total)
              </p>
            </div>
          </>
        )}

        {/* Status feedback */}
        {justSaved && (
          <p className="text-sm text-green-500 font-medium">Settings saved</p>
        )}
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
      </div>
    </div>
  );
};

export default ReadinessAlertSettings;
